import { FormInstance, message } from 'antd';
import { seriesService } from 'content/service';
import { SeriesInterface } from 'content/type/seriesType';
import { NavigateFunction } from 'react-router-dom';

interface UseSeriesDetailProps {
  form: FormInstance;
  navigate: NavigateFunction;
  selectedCategories: number[];
  setIsModalVisible: (value: boolean) => void;
}

interface DetailHandlers {
  handleOnClickAddButtonClick: () => Promise<void>;
  handleOnClickEditButtonClick: () => Promise<void>;
  handleEditCategories: () => void;
  handleModalOk: () => void;
  handleModalCancel: () => void;
}

const handleOnClickAddButtonClick = (props: UseSeriesDetailProps) => async (): Promise<void> => {
  const { form, navigate, selectedCategories } = props;
  const loadingMessage = message.loading('Creating...', 0);
  try {
    await form.validateFields();

    const newSeries: SeriesInterface = {
      id: -1,
      title: form.getFieldValue('title'),
      slug: form.getFieldValue('slug'),
      ...(form.getFieldValue('synopsis') && { synopsis: form.getFieldValue('synopsis') }),
      synopsis_en: form.getFieldValue('synopsis_en'),
      title_en: form.getFieldValue('title_en'),
      status: form.getFieldValue('status'),
      dubbing_mode: form.getFieldValue('dubbing_mode'),
      age_rating: form.getFieldValue('age_rating'),
      ...(form.getFieldValue('season_order') && {
        season_order: form.getFieldValue('season_order'),
      }),

      ...(form.getFieldValue('release_date_start') && {
        release_date_start: form.getFieldValue('release_date_start').format('YYYY-MM-DD'),
      }),

      ...(form.getFieldValue('release_date_end') && {
        release_date_end: form.getFieldValue('release_date_end').format('YYYY-MM-DD'),
      }),
      ...(form.getFieldValue('produce_country') && {
        produce_country: form.getFieldValue('produce_country'),
      }),
      ...(form.getFieldValue('cast') && { cast: form.getFieldValue('cast') }),
      ...(form.getFieldValue('enhanced_cast') && { enhanced_cast: form.getFieldValue('enhanced_cast') }),
      categories: selectedCategories,
      i18n_title: form.getFieldValue('i18n_title'),
      i18n_synopsis: form.getFieldValue('i18n_synopsis'),
      i18n_seo_title: form.getFieldValue('i18n_seo_title'),
      i18n_seo_synopsis: form.getFieldValue('i18n_seo_synopsis'),
      i18n_seo_keyword: form.getFieldValue('i18n_seo_keyword'),
    };
    try {
      const series = await seriesService.create(newSeries);
      if (!series) {
        message.error('Failed to create series');
        return;
      }
      navigate(`/series/edit/${series.id}`);
    } catch {
      loadingMessage();
    }
  } catch {
    message.error('Failed to update.');
  }
};

const handleOnClickEditButtonClick = (props: UseSeriesDetailProps) => async (): Promise<void> => {
  const { form, selectedCategories } = props;
  const loadingMessage = message.loading('Updating...', 0);

  try {
    await form.validateFields();
    const id = form.getFieldValue('id');
    const newSeries: SeriesInterface = {
      id: Number(id),
      title: form.getFieldValue('title'),
      // title_en: form.getFieldValue('title_en'),
      status: form.getFieldValue('status'),
      dubbing_mode: form.getFieldValue('dubbing_mode'),
      slug: form.getFieldValue('slug'),
      synopsis_en: form.getFieldValue('synopsis_en'),
      ...(form.getFieldValue('synopsis') && { synopsis: form.getFieldValue('synopsis') }),
      age_rating: form.getFieldValue('age_rating'),
      ...(form.getFieldValue('release_date_start') && {
        release_date_start: form.getFieldValue('release_date_start').format('YYYY-MM-DD'),
      }),
      ...(form.getFieldValue('release_date_end') && {
        release_date_end: form.getFieldValue('release_date_end').format('YYYY-MM-DD'),
      }),
      ...(form.getFieldValue('produce_country') && {
        produce_country: form.getFieldValue('produce_country'),
      }),
      ...(form.getFieldValue('cast') && { cast: form.getFieldValue('cast') }),
      ...(form.getFieldValue('enhanced_cast') && { enhanced_cast: form.getFieldValue('enhanced_cast') }),
      categories: selectedCategories,
      i18n_title: form.getFieldValue('i18n_title'),
      i18n_synopsis: form.getFieldValue('i18n_synopsis'),
      i18n_seo_title: form.getFieldValue('i18n_seo_title'),
      i18n_seo_synopsis: form.getFieldValue('i18n_seo_synopsis'),
      i18n_seo_keyword: form.getFieldValue('i18n_seo_keyword'),
    };
    try {
      await seriesService.update(Number(id), newSeries);
      message.success('Updated successfully');
      window.location.reload();
    } catch {
      loadingMessage();
    }
  } catch {
    loadingMessage();
    message.error('Failed to update.');
  }
};

const handleEditCategories = (props: UseSeriesDetailProps) => (): void => {
  const { setIsModalVisible } = props;
  setIsModalVisible(true);
};

const handleModalOk = (props: UseSeriesDetailProps) => (): void => {
  const { form, selectedCategories, setIsModalVisible } = props;
  form.setFieldsValue({ categories: selectedCategories });
  setIsModalVisible(false);
};

const handleModalCancel = (props: UseSeriesDetailProps) => (): void => {
  const { setIsModalVisible } = props;
  setIsModalVisible(false);
};

const detailHandlers = (props: UseSeriesDetailProps): DetailHandlers => ({
  handleOnClickAddButtonClick: handleOnClickAddButtonClick(props),
  handleOnClickEditButtonClick: handleOnClickEditButtonClick(props),
  handleEditCategories: handleEditCategories(props),
  handleModalOk: handleModalOk(props),
  handleModalCancel: handleModalCancel(props),
});

export default detailHandlers;
